import React, { useMemo, useState } from 'react';
import Icon from '../../../components/openplay/OpenPlayIcon';
import AppImage from '../../../components/openplay/OpenPlayImage';

const EventHeader = ({
  sportType,
  sportIcon,
  organizationName,
  organizationLogo,
  organizationLogoAlt,
  coverImageUrl,
  imageUrls,
  location,
  currentParticipants,
  maxParticipants,
  rating,
  totalEvents,
}) => {
  const availabilityPercentage = (currentParticipants / maxParticipants) * 100;
  const isFull = currentParticipants >= maxParticipants;
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const gallery = useMemo(() => {
    const list = [coverImageUrl, ...(Array.isArray(imageUrls) ? imageUrls : [])]
      .map((u) => String(u || '').trim())
      .filter(Boolean);
    return [...new Set(list)];
  }, [coverImageUrl, imageUrls]);
  const previewImage = gallery[0] || '';

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <div className="relative flex-shrink-0">
          <div className="w-16 h-16 rounded-xl overflow-hidden bg-muted">
            <AppImage src={organizationLogo} alt={organizationLogoAlt} className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-1 -right-1 flex items-center justify-center w-8 h-8 rounded-lg bg-primary shadow-elevation-2">
            <Icon name={sportIcon} variant="solid" size={16} className="text-primary-foreground" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center px-2 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium caption">
              {sportType}
            </span>
            <span
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium caption ${
                isFull ? 'bg-error text-error-foreground' : 'bg-success text-success-foreground'
              }`}
            >
              {isFull ? '마감' : '모집중'}
            </span>
          </div>
          <h1 className="text-xl font-bold text-foreground mb-1 truncate">{organizationName}</h1>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Icon name="StarIcon" variant="solid" size={16} className="text-warning" />
              <span className="font-medium data-text">{Number(rating).toFixed(1)}</span>
            </div>
            <span className="caption">·</span>
            <span className="caption">진행 {totalEvents}회</span>
          </div>
        </div>
      </div>

      {previewImage && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-muted">
          <button
            type="button"
            className="relative w-20 h-20 rounded-xl overflow-hidden bg-background/40 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary/60"
            onClick={() => {
              setImageIndex(0);
              setShowImageModal(true);
            }}
            aria-label="이벤트 이미지 크게 보기"
          >
            <AppImage src={previewImage} alt={organizationName} className="w-full h-full object-cover" />
            {gallery.length > 1 && (
              <span className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-black/70 text-white text-[11px] font-semibold">
                {gallery.length}장
              </span>
            )}
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground mb-1">대표 이미지</p>
            <p className="text-sm text-muted-foreground caption">클릭해서 크게 보기</p>
          </div>
        </div>
      )}

      <div className="flex items-start gap-3 p-4 rounded-lg bg-muted">
        <Icon name="MapPinIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground mb-1">위치</p>
          <p className="text-sm text-muted-foreground">{location}</p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground caption">참가 현황</span>
          <span className="font-semibold text-foreground data-text">
            {currentParticipants} / {maxParticipants}명
          </span>
        </div>
        <div className="relative h-2 rounded-full bg-muted overflow-hidden">
          <div
            className={`absolute inset-y-0 left-0 rounded-full transition-smooth ${isFull ? 'bg-error' : 'bg-success'}`}
            style={{ width: `${Math.min(availabilityPercentage, 100)}%` }}
          />
        </div>
      </div>

      {showImageModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 md:p-6">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowImageModal(false)} />
          <div className="relative w-full max-w-3xl bg-card rounded-2xl border border-border shadow-elevation-3 overflow-hidden">
            {(() => {
              const safeIndex = Math.min(imageIndex, Math.max(gallery.length - 1, 0));
              const activeImage = gallery[safeIndex] || '';
              const handlePrev = () => {
                setImageIndex((prev) => (prev <= 0 ? gallery.length - 1 : prev - 1));
              };
              const handleNext = () => {
                setImageIndex((prev) => (prev + 1) % gallery.length);
              };
              return (
                <div className="relative">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <p className="text-sm font-semibold text-foreground">이벤트 이미지</p>
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:text-foreground"
                      onClick={() => setShowImageModal(false)}
                    >
                      닫기
                    </button>
                  </div>
                  <div className="relative bg-black">
                    <img src={activeImage} alt="event" className="w-full max-h-[70vh] object-contain" />
                    {gallery.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={handlePrev}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center"
                          aria-label="이전 이미지"
                        >
                          <Icon name="ChevronLeftIcon" size={20} />
                        </button>
                        <button
                          type="button"
                          onClick={handleNext}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center"
                          aria-label="다음 이미지"
                        >
                          <Icon name="ChevronRightIcon" size={20} />
                        </button>
                      </>
                    )}
                  </div>
                  <div className="px-4 py-3 text-xs text-muted-foreground">
                    {gallery.length > 0 ? `${safeIndex + 1} / ${gallery.length}` : '이미지가 없습니다.'}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventHeader;
